import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button, Input } from '../components/ui/Forms';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

export default function Account() {
  const { user, profile, refreshProfile } = useAuth();
  
  // Local state for the form
  const [formData, setFormData] = useState({
    full_name: '',
    avatar_url: ''
  });
  
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  // CRITICAL: Sync local state when profile loads or changes
  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        avatar_url: profile.avatar_url || ''
      });
    }
  }, [profile]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!user) return;
    
    setLoading(true);
    setMsg({ type: '', text: '' });

    try {
      // Use UPSERT instead of UPDATE to handle cases where the profile row might be missing
      // We also include the ID and Email to ensure the row is correctly identified/created
      const { data, error, status } = await supabase
        .from('profiles_20240520')
        .upsert({
          id: user.id,
          email: user.email,
          full_name: formData.full_name,
          avatar_url: formData.avatar_url,
          // We DON'T update the role here for security
        }, {
          onConflict: 'id'
        })
        .select();

      if (error) {
        setMsg({ type: 'error', text: error.message });
      } else {
        setMsg({ type: 'success', text: 'Profile saved successfully!' });
        await refreshProfile();
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'An unexpected error occurred.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      setMsg({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ 
      password: passwords.new 
    });
    
    if (error) {
      setMsg({ type: 'error', text: error.message });
    } else {
      setMsg({ type: 'success', text: 'Password changed successfully!' });
      setPasswords({ current: '', new: '', confirm: '' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Account Details</h1>
        <p className="text-slate-500">Manage your profile and security settings.</p>
      </div>

      {msg.text && (
        <div className={`p-4 rounded-xl text-sm flex items-center gap-2 animate-in fade-in slide-in-from-top-2 ${
          msg.type === 'error' 
            ? 'bg-red-50 text-red-600 border border-red-100' 
            : 'bg-teal-50 text-teal-600 border border-teal-100'
        }`}>
          <SafeIcon name={msg.type === 'error' ? 'AlertCircle' : 'CheckCircle'} className="w-4 h-4" />
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">Profile Information</h2>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="flex items-center gap-6 mb-6">
                <div className="w-24 h-24 rounded-full bg-slate-100 overflow-hidden border-4 border-white shadow-md flex-shrink-0">
                  {formData.avatar_url ? (
                    <img src={formData.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <SafeIcon name="User" className="w-12 h-12" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Input 
                    label="Avatar Image URL" 
                    value={formData.avatar_url} 
                    onChange={e => setFormData({ ...formData, avatar_url: e.target.value })} 
                    placeholder="https://..." 
                  />
                </div>
              </div>

              <Input 
                label="Full Name" 
                value={formData.full_name} 
                onChange={e => setFormData({ ...formData, full_name: e.target.value })} 
                placeholder="Your name"
              />
              
              <Input 
                label="Email Address" 
                value={user?.email || ''} 
                disabled 
              />
              
              <div className="pt-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>

          <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">Security</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <Input 
                label="New Password" 
                type="password" 
                value={passwords.new} 
                onChange={e => setPasswords({ ...passwords, new: e.target.value })} 
                placeholder="Min 6 characters"
              />
              <Input 
                label="Confirm New Password" 
                type="password" 
                value={passwords.confirm} 
                onChange={e => setPasswords({ ...passwords, confirm: e.target.value })} 
                placeholder="Repeat new password"
              />
              <div className="pt-2">
                <Button type="submit" variant="secondary" disabled={loading}>
                  Change Password
                </Button>
              </div>
            </form>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 p-6 rounded-3xl text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-teal-500 rounded-lg">
                <SafeIcon name="Shield" className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold">Account Role</h3>
            </div>
            <p className="text-slate-400 text-sm mb-4">Your current access level determines what features you can see.</p>
            <div className="inline-block px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-xs font-bold uppercase tracking-widest border border-teal-500/30">
              {profile?.role || 'User'}
            </div>
          </div>
          
          <div className="bg-teal-50 p-6 rounded-3xl border border-teal-100">
            <h4 className="font-bold text-teal-800 mb-2 flex items-center gap-2">
              <SafeIcon name="Info" className="w-4 h-4" />
              Profile Tip
            </h4>
            <p className="text-teal-700 text-xs leading-relaxed">
              Your profile information is used for receipt generation and shared workspace features. Keep it up to date for better record keeping.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}